import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { hasCurrentConsent, recordConsent } from "@/services/consent-service";
import { Button } from "@/components/ui/button";
import { Shield, X } from "lucide-react";

/**
 * Shown to logged-in users who haven't acknowledged the current privacy policy version.
 * Dismissing records their acknowledgment in the DB.
 */
export const PrivacyAcknowledgmentBanner = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    hasCurrentConsent(user.id).then((has) => {
      if (!cancelled && !has) setVisible(true);
    });

    return () => { cancelled = true; };
  }, [user?.id]);

  const handleAcknowledge = async () => {
    if (!user) return;
    await recordConsent(user.id, "acknowledgment", language);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 bg-card border-t border-border shadow-lg animate-fade-in">
      <div className="max-w-xl mx-auto flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-foreground mb-1">{t("privacy.inAppNoticeTitle")}</p>
            <p className="text-[11px] leading-relaxed text-muted-foreground">{t("privacy.inAppNotice")}</p>
            <p className="text-[10px] text-muted-foreground/70 mt-1">
              {language === "en" ? "Read our full " : "Lee nuestra "}
              <Link to="/legal/privacy" className="text-primary hover:underline">
                {language === "en" ? "Privacy Policy" : "Política de Privacidad"}
              </Link>
              {language === "en" ? " and " : " y "}
              <Link to="/legal/terms" className="text-primary hover:underline">
                {language === "en" ? "Terms of Service" : "Términos de Servicio"}
              </Link>.
            </p>
          </div>
          <button onClick={handleAcknowledge} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Button size="sm" onClick={handleAcknowledge} className="w-full">
          {language === "en" ? "I understand" : "Entendido"}
        </Button>
      </div>
    </div>
  );
};
